import { useState } from 'react'

import { CheckIcon } from 'lucide-react'
import type { Control, FieldPath, FieldPathValue, FieldValues } from 'react-hook-form'

import { Render } from '@/components/render-if'
import { Button } from '@/components/ui/button'
import { ChevronIndicator } from '@/components/ui/chevron-indicator'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useIsMobile } from '@/hooks'
import { cn } from '@/infra'

type TItemSelecionado<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> =
  FieldPathValue<TFieldValues, TName> extends ReadonlyArray<infer TItem> ? TItem : never

type TChaveDoValor<TOption, TItem> = {
  [TChave in keyof TOption]: TOption[TChave] extends TItem ? TChave : never
}[keyof TOption]

type TMultiSelectFieldProps<
  TOption extends Record<string, unknown>,
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = {
  control?: Control<TFieldValues, TContext, TTransformedValues>
  name: TName
  label: string
  options: TOption[]
  labelKey: keyof TOption
  valueKey: TChaveDoValor<TOption, TItemSelecionado<TFieldValues, TName>>
  placeholder: string
  placeholderBusca: string
  mensagemVazia: string
  formatarResumoDaSelecao: (quantidade: number) => string
  disabled?: boolean
  className?: string
  'data-testid'?: string
}

export const MultiSelectField = <
  TOption extends Record<string, unknown>,
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  control,
  name,
  label,
  options,
  labelKey,
  valueKey,
  placeholder,
  placeholderBusca,
  mensagemVazia,
  formatarResumoDaSelecao,
  disabled,
  className,
  'data-testid': dataTestId,
}: TMultiSelectFieldProps<TOption, TFieldValues, TName, TContext, TTransformedValues>) => {
  const [aberto, setAberto] = useState(false)
  const isMobile = useIsMobile()

  const rotuloDaOpcao = (option: TOption) => String(option[labelKey])

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selecionados: unknown[] = field.value ?? []

        const rotuloDoCampo = () => {
          if (!selecionados.length) return placeholder
          if (selecionados.length === 1) {
            const unicaOpcao = options.find((option) => option[valueKey] === selecionados[0])
            return unicaOpcao ? rotuloDaOpcao(unicaOpcao) : placeholder
          }
          return formatarResumoDaSelecao(selecionados.length)
        }

        const alternarSelecao = (valor: unknown) =>
          field.onChange(
            selecionados.includes(valor)
              ? selecionados.filter((selecionado) => selecionado !== valor)
              : [...selecionados, valor],
          )

        const fecharEValidar = (proximoEstado: boolean) => {
          setAberto(proximoEstado)
          if (!proximoEstado) field.onBlur()
        }

        return (
          <FormItem className="relative">
            <FormLabel aria-disabled={disabled}>{label}</FormLabel>
            <Popover open={aberto} onOpenChange={fecharEValidar}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={dataTestId}
                    aria-expanded={aberto}
                    data-opened={String(aberto)}
                    data-selected={String(selecionados.length > 0)}
                    disabled={disabled}
                    className={cn(
                      'border-input truncate bg-neutral-50 px-3 text-base font-normal text-neutral-500 shadow-none hover:text-neutral-600',
                      'focus-visible:ring-primary outline-offset-0 outline-none',
                      'aria-invalid:border-destructive',
                      'data-[opened=true]:border-primary data-[opened=true]:border',
                      '[&:not(:disabled)[data-selected=true]]:bg-slate-100 [&:not(:disabled)[data-selected=true]]:text-gray-800',
                      'disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-500 disabled:opacity-100',
                      className,
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate">{rotuloDoCampo()}</span>
                      <ChevronIndicator open={aberto} className="text-muted-foreground/80" />
                    </div>
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                className="border-input w-full min-w-(--radix-popper-anchor-width) p-0"
                align={isMobile ? 'center' : 'start'}
              >
                <Command
                  shouldFilter
                  filter={(_, busca, keywords) =>
                    Number(keywords?.some((keyword) => keyword.toLowerCase().includes(busca.toLowerCase())))
                  }
                >
                  <CommandInput placeholder={placeholderBusca} />
                  <CommandList>
                    <CommandEmpty>{mensagemVazia}</CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => {
                        const valor = option[valueKey]
                        const selecionado = selecionados.includes(valor)

                        return (
                          <CommandItem
                            key={String(valor)}
                            keywords={[rotuloDaOpcao(option)]}
                            data-filled={selecionado}
                            onSelect={() => alternarSelecao(valor)}
                            className="hover:bg-accent flex cursor-pointer items-center justify-between"
                          >
                            {rotuloDaOpcao(option)}
                            <Render.If condition={selecionado}>{() => <CheckIcon size={16} />}</Render.If>
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
